/**
 * The delivery of each message this chat sends while the agent works
 * (mission stream contract phase 6, agion/Agion#2327). The message goes out
 * as mission.user.message with a fresh message id, and its receipts, keyed by
 * that id, move it through the generated delivery states with
 * nextDeliveryState and receiptEvent (@agion/mission-stream):
 *
 * - sending: the frame left the browser;
 * - queued: the host's inbox took it (receipt accepted), and the agent reads
 *   it at its next turn; its timer stops, however long the turn runs;
 * - delivered: the agent got it (receipt delivered); the message moves to
 *   where the agent got it, as the history row written then lists it on
 *   reload (placeAtDelivery);
 * - refused: the relay or the host refused it (receipt refused), with the
 *   typed reason;
 * - failed: the socket was down; no receipt came within
 *   ANSWER_RECEIPT_TIMEOUT_MS (the host may be offline, or older and drop
 *   the frame; the app cannot tell which, and there is no version check); or
 *   the run ended first (receipt undelivered, or the run's own end).
 *
 * The receipt timeout is the answer's, 20 seconds: the host accepts or
 * refuses the message when it reads the frame and waits for nothing else, so
 * the accepted receipt takes the same path back as an answer's echo.
 *
 * A failed or refused message goes back to the composer with its text and
 * files, through the returned answer. Receipts never move a message back.
 * Every callback is stable, because the chat builds its stream handlers once.
 *
 * A page that reloads while its messages wait learns them from the host
 * (challenge record Mid-Eval P6-2): after every subscribe the host lists the
 * user's messages that still wait (mission.user.message.queued), and
 * restoreQueued shows each one this chat does not track as queued again, with
 * the row and id a live send has, so its later receipts move it as usual. The
 * browser keeps no copy of its own. The chat's two list resets, the subscribe
 * reset and the history load, keep the rows of messages that still wait for
 * the agent (keptRows).
 */

import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import {
  ANSWER_RECEIPT_TIMEOUT_MS,
  MessageTypeUserMessage,
  awaitsReceipt,
  nextDeliveryState,
  placeAtDelivery,
  receiptEvent,
  returnsToComposer,
  streamMessageKind,
  unknownKind,
  type DeliveryEvent,
  type DeliveryState,
  type UserMessageQueuedView,
  type UserMessageReceiptView,
} from '@agion/mission-stream'
import { getMissionStatusService } from '../services/streaming/mission-status'
import type { HITLAttachment } from '../types/streaming'
import type { MessageDelivery, ReturnedAnswer } from './useAnswerDelivery'
import type { StreamingChatMessage } from './useStreamingMissionChat'

interface TrackedMessage {
  /** The message id on the wire, the host's de-duplication key. */
  messageId: string
  /** The id of the chat message that shows it. */
  chatMessageId: string
  /** The run it was sent to. */
  executionId: string
  text: string
  attachments: HITLAttachment[]
  delivery: MessageDelivery
  timer?: ReturnType<typeof setTimeout>
}

interface UseUserMessageDeliveryOptions {
  setMessages: Dispatch<SetStateAction<StreamingChatMessage[]>>
  /** Hands a failed or refused message back to the composer. Must be stable. */
  returnSend: (returned: ReturnedAnswer) => void
}

export interface UserMessageDelivery {
  /** Sends a message to the run while the agent works and tracks its delivery. */
  sendUserMessage: (missionId: string, executionId: string, text: string, attachments: HITLAttachment[]) => void
  /** A receipt; true when it was for a message this chat sent. */
  userMessageReceipt: (receipt: UserMessageReceiptView) => boolean
  /** The run ended: a message it had not delivered fails with that reason. */
  runEnded: () => void
  /**
   * The host's list of this user's messages that still wait: each one this
   * chat does not track shows as queued, as a live send does.
   */
  restoreQueued: (queued: UserMessageQueuedView) => void
  /** The rows among `rows` of messages sent to the execution that still wait for the agent. */
  keptRows: (executionId: string | null, rows: readonly StreamingChatMessage[]) => StreamingChatMessage[]
  /**
   * Forgets every tracked message, for a new execution, except the ones sent
   * to `keepExecutionId` that still wait for the agent.
   */
  reset: (keepExecutionId?: string) => void
}

/**
 * Whether a message in this state still waits for the agent: the chat shows
 * it until its delivery, refusal or failure. A delivered message reloads from
 * the history, and a failed or refused one went back to the composer.
 */
function awaitsDelivery(state: DeliveryState): boolean {
  switch (state) {
    case 'waiting':
    case 'sending':
    case 'queued':
      return true
    case 'delivered':
    case 'failed':
    case 'refused':
      return false
    default:
      return unknownKind(state, false)
  }
}

/** The chat row of a tracked message: a user row, as its history row reloads. */
function chatRow(message: TrackedMessage, missionId: string): StreamingChatMessage {
  return {
    id: message.chatMessageId,
    type: streamMessageKind(MessageTypeUserMessage),
    timestamp: Date.now(),
    missionId,
    data: {
      // A user row reloads without a question (StreamingCustomerChat history).
      requestId: '',
      response: message.text,
      // Display facts only: the row names each file and downloads nothing.
      attachments: message.attachments.length
        ? message.attachments.map((a) => ({ filename: a.filename, contentType: a.content_type, size: a.size }))
        : undefined,
      deliveryOrigin: 'composer',
      delivery: message.delivery,
      // The member's own message: sent here, or listed by the host as this user's (agion/Agion#2039).
      sentByThisChat: true,
    },
  }
}

/** What a receipt's event records beside the new state. */
function receiptWhy(event: DeliveryEvent, receipt: UserMessageReceiptView): Omit<MessageDelivery, 'state'> {
  if (event === 'refusal') return receipt.reason ? { reason: receipt.reason } : {}
  if (event === 'undelivered') return { failure: 'undelivered', ...(receipt.reason ? { reason: receipt.reason } : {}) }
  return {}
}

export function useUserMessageDelivery({ setMessages, returnSend }: UseUserMessageDeliveryOptions): UserMessageDelivery {
  const messagesRef = useRef(new Map<string, TrackedMessage>())

  const apply = useCallback((message: TrackedMessage, event: DeliveryEvent, why: Omit<MessageDelivery, 'state'> = {}) => {
    const before = message.delivery.state
    const state = nextDeliveryState(before, event)
    if (state === before) return
    const delivery: MessageDelivery = { state, ...why }
    message.delivery = delivery
    if (!awaitsReceipt(state) && message.timer !== undefined) {
      clearTimeout(message.timer)
      message.timer = undefined
    }
    const deliveredAt = Date.now()
    setMessages((prev) => {
      const updated = prev.map((m) => (m.id === message.chatMessageId ? { ...m, data: { ...m.data, delivery } } : m))
      return state === 'delivered' ? [...placeAtDelivery(updated, message.chatMessageId, deliveredAt)] : updated
    })
    if (returnsToComposer(state) && !returnsToComposer(before)) {
      returnSend({
        id: `${message.chatMessageId}-${state}`,
        origin: 'composer',
        text: message.text,
        attachments: message.attachments,
        delivery,
      })
    }
  }, [setMessages, returnSend])

  const sendUserMessage = useCallback<UserMessageDelivery['sendUserMessage']>((missionId, executionId, text, attachments) => {
    const messageId = crypto.randomUUID()
    const message: TrackedMessage = {
      messageId,
      chatMessageId: `user-message-${messageId}`,
      executionId,
      text,
      attachments,
      delivery: { state: 'waiting' },
    }
    messagesRef.current.set(messageId, message)
    setMessages((prev) => [...prev, chatRow(message, missionId)])

    const sent = getMissionStatusService().sendUserMessage({
      message_id: messageId,
      mission_id: missionId,
      execution_id: executionId,
      text,
      // Carry upload provenance for host admission; the host reads display
      // facts from the file record itself.
      ...(attachments.length
        ? { attachments }
        : {}),
    })
    // Only an explicit false means nothing left the browser.
    if (sent === false) {
      apply(message, 'not_sent', { failure: 'not_connected' })
      return
    }
    apply(message, 'sent')
    message.timer = setTimeout(() => apply(message, 'timeout', { failure: 'not_accepted' }), ANSWER_RECEIPT_TIMEOUT_MS)
  }, [setMessages, apply])

  const userMessageReceipt = useCallback((receipt: UserMessageReceiptView): boolean => {
    const message = receipt.message_id ? messagesRef.current.get(receipt.message_id) : undefined
    if (!message) return false
    const event = receiptEvent(receipt.outcome)
    if (event) apply(message, event, receiptWhy(event, receipt))
    return true
  }, [apply])

  const runEnded = useCallback(() => {
    for (const message of messagesRef.current.values()) {
      apply(message, 'undelivered', { failure: 'undelivered', reason: 'run_ended_before_delivery' })
    }
  }, [apply])

  const restoreQueued = useCallback((queued: UserMessageQueuedView) => {
    const restored: TrackedMessage[] = []
    for (const entry of queued.messages ?? []) {
      if (!entry.message_id || messagesRef.current.has(entry.message_id)) continue
      const message: TrackedMessage = {
        messageId: entry.message_id,
        chatMessageId: `user-message-${entry.message_id}`,
        executionId: queued.execution_id,
        text: entry.text,
        // Retain provenance when a restored message returns to the composer.
        attachments: entry.attachments ?? [],
        // The host's inbox holds it: queued, with no timer.
        delivery: { state: 'queued' },
      }
      messagesRef.current.set(message.messageId, message)
      restored.push(message)
    }
    if (restored.length === 0) return
    setMessages((prev) => {
      const shown = new Set(prev.map((m) => m.id))
      const rows = restored.filter((m) => !shown.has(m.chatMessageId)).map((m) => chatRow(m, queued.mission_id))
      return rows.length ? [...prev, ...rows] : prev
    })
  }, [setMessages])

  const keptRows = useCallback((executionId: string | null, rows: readonly StreamingChatMessage[]) => {
    if (!executionId) return []
    const waiting = new Set<string>()
    for (const message of messagesRef.current.values()) {
      if (message.executionId === executionId && awaitsDelivery(message.delivery.state)) waiting.add(message.chatMessageId)
    }
    return rows.filter((row) => waiting.has(row.id))
  }, [])

  const reset = useCallback((keepExecutionId?: string) => {
    const kept = new Map<string, TrackedMessage>()
    for (const message of messagesRef.current.values()) {
      if (keepExecutionId && message.executionId === keepExecutionId && awaitsDelivery(message.delivery.state)) {
        kept.set(message.messageId, message)
      } else if (message.timer !== undefined) {
        clearTimeout(message.timer)
      }
    }
    messagesRef.current = kept
  }, [])

  useEffect(() => () => {
    for (const message of messagesRef.current.values()) {
      if (message.timer !== undefined) clearTimeout(message.timer)
    }
  }, [])

  return { sendUserMessage, userMessageReceipt, runEnded, restoreQueued, keptRows, reset }
}
